import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ShieldOff, Search, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const REASON_LABEL = {
  hardBounce: "Bounce definitivo",
  hard_bounce: "Bounce definitivo",
  unsubscribed: "Disiscritto",
  unsubscribe: "Disiscritto",
  spam: "Segnalato spam",
  blocked: "Bloccato",
};

export default function Soppressioni() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const pageSize = 50;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  const load = useCallback(() => {
    setLoading(true);
    api.get("/brevo/suppressions", { params: { q: q || undefined, page, page_size: pageSize } })
      .then((r) => { setItems(r.data.items); setTotal(r.data.total); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [q, page]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { setPage(1); }, [q]);

  const reactivate = async (email) => {
    if (!window.confirm(`Rimuovere ${email} dalla lista di soppressione? Tornerà a ricevere le email.`)) return;
    try {
      await api.delete(`/brevo/suppressions/${encodeURIComponent(email)}`);
      toast.success(`${email} riattivato`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); }
  };

  const fmtDate = (s) => { try { return new Date(s).toLocaleString("it-IT"); } catch { return s || "—"; } };

  return (
    <div className="space-y-6" data-testid="soppressioni-page">
      <button onClick={() => navigate("/marketing")} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-marketing">
        <ArrowLeft className="h-4 w-4" /> Torna al Marketing
      </button>

      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
          <ShieldOff className="h-6 w-6 text-primary" /> Lista di Soppressione
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {total} indirizzi esclusi dagli invii (bounce, disiscrizioni e segnalazioni spam raccolti automaticamente da Brevo). Questi contatti vengono saltati durante la sincronizzazione delle campagne.
        </p>
      </div>

      <Card className="p-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="supp-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per email…" className="pl-9" />
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="soppressioni-table">
            <thead className="bg-muted/50 border-b border-border">
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3 font-semibold">Email</th>
                <th className="px-4 py-3 font-semibold">Motivo</th>
                <th className="px-4 py-3 font-semibold">Data</th>
                <th className="px-4 py-3 font-semibold text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id || s.email} data-testid={`supp-row-${s.email}`} className="border-b border-border last:border-0">
                  <td className="px-4 py-3 font-medium break-all">{s.email}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center rounded-full bg-destructive/10 text-destructive px-2.5 py-0.5 text-xs font-semibold">
                      {REASON_LABEL[s.reason] || s.reason}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{fmtDate(s.updated_at || s.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="outline" size="sm" data-testid={`supp-reactivate-${s.email}`} onClick={() => reactivate(s.email)}>
                      <RotateCcw className="h-3.5 w-3.5 mr-1.5" />Riattiva
                    </Button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-12 text-center text-muted-foreground">{loading ? "Caricamento…" : "Nessun indirizzo soppresso"}</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {total > 0 && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-border" data-testid="supp-pagination">
            <span className="text-sm text-muted-foreground">{(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} di {total}</span>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" className="h-8 w-8" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="supp-prev"><ArrowLeft className="h-4 w-4" /></Button>
              <span className="px-1 tabular-nums text-sm">{page}/{pageCount}</span>
              <Button variant="outline" size="icon" className="h-8 w-8" disabled={page >= pageCount} onClick={() => setPage((p) => Math.min(pageCount, p + 1))} data-testid="supp-next"><ArrowLeft className="h-4 w-4 rotate-180" /></Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
